import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TableCard from "../../components/ui-components/TableCard";
import EmptyState from "../../components/ui-components/EmptyState";
import Badge from "../../components/ui-components/Badge";
import { useUseCasePortfolioQuery } from "./hooks/useUseCaseQueries";
import { buildDisplayRows } from "./buildTree";
import { statusLabel, statusTone, formatVariance } from "./status";

const money = (value) => {
  if (value == null) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
};

export default function UseCasePortfolioPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useUseCasePortfolioQuery();
  const [collapsed, setCollapsed] = useState(() => new Set());

  const rows = useMemo(
    () => buildDisplayRows(data?.rows ?? [], collapsed),
    [data, collapsed]
  );

  const toggle = (nodeId) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });

  const summary = useMemo(() => {
    const all = data?.rows ?? [];
    // Only roots are summed, so a parent's spend is never counted twice.
    const roots = all.filter((r) => r.parentNodeId == null);
    const spend = roots.reduce((acc, r) => acc + (r.rolledCost ?? 0), 0);
    const atRisk = all.filter((r) => r.status === "red" || r.status === "amber").length;
    const unbudgeted = all.filter((r) => r.status === "unbudgeted").length;
    return { count: all.length, spend, atRisk, unbudgeted };
  }, [data]);

  const isEmpty = !isLoading && !error && rows.length === 0;

  return (
    <div className="p-6">
      <TableCard
        title="Use Cases"
        subtitle={
          isEmpty
            ? undefined
            : `${summary.count} use cases · ${money(summary.spend)} this period · ${summary.atRisk} at risk · ${summary.unbudgeted} unbudgeted`
        }
        isLoading={isLoading}
        error={error}
        errorMessage="Could not load the use-case portfolio"
      >
        {isEmpty && data?.noAccess ? (
          <EmptyState
            title="Your access does not cover use cases"
            message="Your access is confined to parts of other hierarchies. Ask an administrator for a grant on the use-case hierarchy to see this page."
          />
        ) : isEmpty ? (
          <EmptyState
            title="No use cases yet"
            message="Use cases come from the use-case hierarchy. Add nodes to it in Settings, under Dimensions & hierarchy."
          />
        ) : (
          // `theme.width` in tailwind.config.js is REPLACED (it sits outside
          // `extend`) with a four-entry custom scale, so `w-full` compiles to
          // nothing and Tailwind drops it silently — the table rendered at its
          // intrinsic width and every gate still passed. Inline width is the
          // workaround this codebase already documents for exactly this; see
          // RightSheet.jsx's own note and UseCaseProfileSheet's fieldStyle.
          <table className="text-left" style={{ width: "100%" }}>
            <thead className="text-text-2 uppercase text-xs">
              <tr>
                <th className="py-2">Use case</th>
                <th className="py-2">Owner</th>
                <th className="py-2 text-right">Actual</th>
                <th className="py-2 text-right">Budget</th>
                <th className="py-2 text-right">Forecast</th>
                <th className="py-2 text-right">Variance</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.nodeId}
                  className="border-t border-border hover:bg-surface-2 cursor-pointer"
                  onClick={() => navigate(`/use-cases/${row.nodeId}`)}
                >
                  <td className="py-2" style={{ paddingLeft: 8 + row.depth * 20 }}>
                    {row.hasChildren && (
                      <button
                        type="button"
                        aria-label={`${collapsed.has(row.nodeId) ? "Expand" : "Collapse"} ${row.name}`}
                        className="mr-2 text-text-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(row.nodeId);
                        }}
                      >
                        {collapsed.has(row.nodeId) ? "▸" : "▾"}
                      </button>
                    )}
                    <Link
                      to={`/use-cases/${row.nodeId}`}
                      className="hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {row.name}
                    </Link>
                  </td>
                  <td className="py-2">{row.ownerName || "—"}</td>
                  <td className="py-2 text-right">{money(row.rolledCost)}</td>
                  <td className="py-2 text-right">
                    {row.budget ? money(row.budget.capEur) : "—"}
                  </td>
                  <td className="py-2 text-right">{money(row.forecast)}</td>
                  <td className="py-2 text-right">{formatVariance(row.variance)}</td>
                  <td className="py-2">
                    <Badge status={statusTone(row.status)} label={statusLabel(row.status)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableCard>
    </div>
  );
}
